import { z } from 'zod';

/**
 * Contrat wire de l'**état du canvas poussé avec chaque message de l'avocat**
 * (lot A de `docs/features/redaction-web-canvas/specs/2026-08-12-canvas-agent-awareness-design.html`).
 *
 * POURQUOI un push, alors que `canvas_status` existe déjà : l'agent n'appelle
 * pas un outil dont il ignore qu'il devrait l'appeler. Au tour où l'avocat écrit
 * « rédige un mail à mon client » avec le canvas ouvert sous les yeux, rien dans
 * le contexte du modèle ne mentionne le canvas — il répond donc dans le chat
 * (incident du 12/08/2026, conversation 9240c6ca). Seul un signal poussé sans
 * qu'il le demande peut le rendre conscient.
 *
 * POURQUOI le front envoie une STRUCTURE et pas la phrase : la formulation
 * française destinée au modèle est une décision de prompt, elle appartient au
 * backend (`formatCanvasStateBlock`). Le navigateur ne rapporte que des faits.
 */

/**
 * Provenance du document monté dans le canvas, telle que le NAVIGATEUR peut
 * l'établir — c'est-à-dire mieux que le backend (`canvas_status` doit fusionner
 * `vierge` et `genere`, faute de signal qui les sépare).
 *
 * - `vierge` : page blanche, aucun acte streamé, aucun document chargé. ⚠️ C'est
 *   un défaut de classification, pas une lecture de l'éditeur : l'avocat a pu y
 *   taper trois pages. Rien de ce qui consomme cette valeur ne doit affirmer que
 *   le canvas est VIDE ;
 * - `genere` : un plan ou un acte a été (ou est en train d'être) écrit par un job ;
 * - `charge` : un `.docx` du cabinet (ou un brouillon importé) est ouvert.
 */
const CanvasDocumentSourceSchema = z.enum(['vierge', 'genere', 'charge']);
export type CanvasDocumentSource = z.infer<typeof CanvasDocumentSourceSchema>;

/**
 * Longueur maximale du titre transporté. Alignée sur le `maxLength` du champ de
 * renommage (`CanvasTitle.tsx`) : au-delà, l'entrée ne vient pas de l'UI.
 */
const CANVAS_AGENT_STATE_TITLE_MAX = 255;

/**
 * État du canvas au moment où l'avocat envoie son message.
 *
 * `ouvert: false` ⟹ `document: null`, et le backend n'appose **aucun** suffixe :
 * le silence vaut « fermé » (convention écrite dans `prompts/sections/redactionMode.ts`,
 * sans quoi elle serait indevinable pour le modèle).
 */
export const CanvasAgentStateSchema = z.object({
  ouvert: z.boolean(),
  document: z
    .object({
      source: CanvasDocumentSourceSchema,
      /**
       * Titre affiché à l'avocat. `null` quand le document n'a jamais été
       * nommé — un canvas vierge n'a pas de nom, et en inventer un ferait
       * croire au modèle qu'un document identifiable est ouvert.
       *
       * ⚠️ Saisi par l'avocat : le backend le NEUTRALISE avant injection
       * (`formatCanvasStateBlock`). Ne jamais l'interpoler tel quel.
       */
      titre: z.string().max(CANVAS_AGENT_STATE_TITLE_MAX).nullable(),
      /**
       * Le brouillon serveur que ce document OUVERT alimente — l'adresse de ce
       * sur quoi `save_document` doit agir.
       *
       * POURQUOI le navigateur l'annonce, plutôt que le modèle le choisisse :
       * depuis que plusieurs documents coexistent dans une conversation
       * (2026-09-22), « le brouillon du fil » ne désigne plus rien — le serveur
       * résolvait le plus récemment MODIFIÉ, qui n'est pas forcément celui que
       * l'avocat regarde. Donner le choix au modèle aurait été pire : il aurait
       * rangé dans le dossier un document que personne n'avait sous les yeux,
       * et l'avocat n'aurait eu aucun moyen de le prévoir. Un seul document est
       * ouvert à la fois, l'écran est le seul à savoir lequel, il le dit.
       *
       * Trois valeurs, trois sens DISTINCTS — ne pas les confondre :
       * - un uuid : ce brouillon-là, et le serveur ne cherche pas plus loin ;
       * - `null` : le document ouvert n'a encore aucun brouillon durable (page
       *   neuve jamais enregistrée). Le serveur retombe alors sur la résolution
       *   par conversation, car un brouillon créé PENDANT le tour est
       *   nécessairement celui-ci ;
       * - **absent** : cet hôte ne sait pas l'annoncer (add-in Word, onglet
       *   ouvert avant ce déploiement). Même repli, et c'est ce qui rend le
       *   champ `optional()` : l'exiger ferait rejeter le message entier d'un
       *   client plus ancien, pendant toute la fenêtre de rolling deploy.
       *
       * ⚠️ Cet identifiant ne voyage PAS jusqu'au modèle : `formatCanvasStateBlock`
       * n'en dit rien. Le faire lire au modèle l'inviterait à le recopier dans
       * un paramètre, c'est-à-dire à rechoisir ce que l'écran vient de trancher.
       */
      brouillonId: z.string().uuid().nullable().optional(),
    })
    .nullable(),
});
export type CanvasAgentState = z.infer<typeof CanvasAgentStateSchema>;

/**
 * Longueur maximale du texte sélectionné transporté.
 *
 * Même borne que le bloc de sélection de l'add-in Word
 * (`hector-agent-edition-v3/core/MasterLoop.formatSelectionBlock`) : au-delà,
 * l'avocat a sélectionné une portion qu'il ne désigne plus, il travaille dessus.
 * La coupe est annoncée au modèle plutôt que silencieuse — un texte tronqué sans
 * le dire ferait raisonner sur un extrait pris pour le tout.
 */
export const CANVAS_AGENT_SELECTION_TEXT_MAX = 1500;

/**
 * Ce que l'avocat a SÉLECTIONNÉ dans le canvas au moment où il envoie son
 * message — parité avec la sélection Word, qui voyage déjà dans le message
 * (`[SÉLECTION UTILISATEUR — …]`).
 *
 * POURQUOI un push et pas un outil : c'est le même raisonnement que pour l'état
 * du canvas ci-dessus. L'agent n'appelle pas un outil dont il ignore qu'il
 * devrait l'appeler ; « reformule ce paragraphe » n'a de sens que si le
 * paragraphe désigné est déjà sous ses yeux. Un aller-retour d'outil, en plus
 * d'un tour perdu, arriverait trop tard : la sélection du navigateur a bougé
 * dès que l'avocat a cliqué dans le composeur.
 *
 * ⚠️ Le texte vient du DOCUMENT de l'avocat : le backend le neutralise avant
 * injection, comme le titre (`sanitizeStateLabel`). Ne jamais l'interpoler tel quel.
 */
export const CanvasAgentSelectionSchema = z.object({
  /** Texte sélectionné, déjà borné côté navigateur. Jamais vide : une sélection vide n'est pas envoyée. */
  texte: z.string().min(1).max(CANVAS_AGENT_SELECTION_TEXT_MAX),
  /** Vrai si le texte a été coupé à la borne — le backend le DIT au modèle. */
  tronque: z.boolean(),
  /**
   * Identifiant du bloc SuperDoc où commence la sélection, quand il est
   * résolvable. C'est ce que `canvas_perform_action` sait cibler (`nodeId`) —
   * d'où le choix de le transporter plutôt qu'un index de paragraphe, qui
   * exigerait une projection asynchrone du corps entier du document.
   */
  blocId: z.string().nullable(),
});
export type CanvasAgentSelection = z.infer<typeof CanvasAgentSelectionSchema>;
